import React, { createContext, useContext, useEffect, useRef } from 'react'
import { EarnType } from '~/lib/constants'
import {
	EarnHook,
	EarnStatus,
	useEarnContract,
} from '~/contracts/useEarnContract'
import { useWallet, WalletStatus } from '~/lib/useWallet'
import { getChainById, getEarnByType } from '~/lib/config'
import { symbiosis } from '~/lib/symbiosis'

const EarnContext = createContext<{
	contract: EarnHook
	earnType: EarnType
	isWrongChain: boolean
	changeNetwork: () => Promise<void>
}>(null)

export const useEarns = () => useContext(EarnContext)

export const useEarn = () => useContext(EarnContext)

export const EarnProvider: React.FC<{ earnType: EarnType }> = ({
	earnType,
	children,
}) => {
	const wallet = useWallet()

	const earnConfig = getEarnByType(earnType)
	const chainConfig = getChainById(earnConfig.chainId)

	const requireChainId = earnConfig.chainId
	const isCurrentChain = wallet.isCurrentChain(requireChainId)

	const provider = symbiosis.getProvider(requireChainId)

	const earnContract = useEarnContract({
		account: wallet.account,
		chainConfig,
		earnConfig,
		provider,
		signer: wallet.signer,
	})

	const refreshingRef = useRef(false)

	useEffect(() => {
		if (earnContract.status !== EarnStatus.SUCCESS) return

		const handler = () => {
			if (refreshingRef.current) return
			refreshingRef.current = true
			earnContract.refresh().finally(() => {
				refreshingRef.current = false
			})
		}

		provider.on('block', handler)
		return () => {
			provider.off('block', handler)
		}
	}, [earnContract.status, provider])

	useEffect(() => {
		if (!wallet.account) return

		earnContract.load()
	}, [earnType, wallet.account])

	const changeNetwork = () => {
		if (!isCurrentChain) {
			return wallet.setChain(requireChainId)
		} else {
			return Promise.resolve()
		}
	}

	const isWrongChain =
		wallet.status === WalletStatus.CONNECTED && !isCurrentChain

	return (
		<EarnContext.Provider
			value={{
				earnType,
				contract: earnContract,
				isWrongChain,
				changeNetwork,
			}}
			children={children}
		/>
	)
}
